import { useApolloClient } from "@apollo/client";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import { CircularProgress } from "@mui/material";
import IconButton from "@mui/material/IconButton";
import { styled } from "@mui/material/styles";
import { AdapterDayjs } from "@mui/x-date-pickers-pro/AdapterDayjs";
import { LocalizationProvider as MuiPickersUtilsProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { MobileDatePicker, MobileDatePickerProps } from "@mui/x-date-pickers/MobileDatePicker";
import { useInterpret, useSelector } from "@xstate/react";
import isEqual from "lodash/isEqual";
import { useRef } from "react";

import { dayjs, Dayjs } from "@holibob-packages/dayjs";

import { Availability } from "../../apiHooks/useCacheAvailability";
import { WarningMessage } from "../../components";
import { useNextTranslation } from "../../hooks";
import { useLanguage } from "../../hooks/useLanguage";
import {
    AvailabilityByMonth,
    availabilityDatePickerMachine,
    calendarDate,
    toDateString,
    toMonthString,
} from "./AvailabilityDatePicker.machine";

const DatePickerContainer = styled("div")({
    "& .MuiInputBase-root": {
        backgroundColor: "white",
    },
    "& .MuiInputBase-input:not([disabled])": {
        cursor: "pointer",
    },
    "& .MuiButtonBase-root": {
        margin: "0 0.25rem 0 -0.25rem",
    },
});

export type AvailabilityDatePickerProps = {
    productId: string;
    availabilityListItemId: string | null;
    initialAvailability?: Availability | null;
    timeZone?: string | null;
    onChange: (availabilityListItem: string | null, isDefault: boolean) => void;
} & Omit<MobileDatePickerProps<Dayjs>, "value" | "onChange" | "renderInput">;
export const AvailabilityDatePicker = ({
    productId,
    availabilityListItemId,
    initialAvailability = null,
    timeZone = null,
    onChange,
    ...props
}: AvailabilityDatePickerProps) => {
    const language = useLanguage();

    // Get calendar in the product timezone
    // If timezone is not provided, assume UTC
    const nowTz = timeZone ? dayjs().tz(timeZone) : dayjs().utc(false);
    const initialAvailabilityDate = initialAvailability?.date && new Date(initialAvailability.date);
    const calendarMonth = initialAvailabilityDate
        ? calendarDate({ year: initialAvailabilityDate.getFullYear(), month: initialAvailabilityDate.getMonth() + 1 })
        : calendarDate({ year: nowTz.get("year"), month: nowTz.get("month") + 1 });

    const service = useInterpret(availabilityDatePickerMachine, {
        context: {
            apolloClient: useApolloClient(),
            productId,
            timeZone,
            calendarMonth,
            initialAvailability,
        },
        actions: {
            notifyNoAvailabilityInFirstYear: () => {
                onChange(null, false);
            },
            notifyDefaultAvailabilityItemFound: (ctx) => {
                // this should always exist, but we need the check for typescript
                if (ctx.defaultAvailabilityListItem) {
                    onChange(ctx.defaultAvailabilityListItem.id, true);
                }
            },
        },
    });
    const send = service.send;
    const [t] = useNextTranslation("availability");

    const availabilityList = useSelector(
        service,
        (state) =>
            Object.values(state.context.availabilityListByMonth)
                .filter((abm): abm is AvailabilityByMonth => typeof abm !== "undefined")
                .map((abm) => abm.availabilityList)
                .flat(),
        isEqual
    );
    const defaultAvailabilityListItem = useSelector(service, (state) => state.context.defaultAvailabilityListItem);
    const validDates = useSelector(service, (state) => state.context.validDates);
    const availabilityListByMonth = useSelector(service, (state) => state.context.availabilityListByMonth);
    const warningMessages = useSelector(service, (state) => state.context.warningMessages);

    const isCurrentMonthPending = useSelector(
        service,
        (state) =>
            state.context.availabilityListByMonth[toMonthString(state.context.calendarMonth)]?.state === "pending"
    );
    const isLoading = useSelector(service, (state) => state.matches("searchingForAvailability"));
    const noAvailabilityInFirstYear = useSelector(service, (state) => state.matches("noAvailabilityInFirstYear"));
    const isDisabled = isLoading || noAvailabilityInFirstYear;

    const inputRef = useRef<HTMLInputElement>(null);
    return (
        <>
            <MuiPickersUtilsProvider adapterLocale={language.codeShort} dateAdapter={AdapterDayjs}>
                <DatePickerContainer>
                    <MobileDatePicker
                        // display settings
                        disabled={isDisabled}
                        // today is differnet per timezone
                        disableHighlightToday={true}
                        format="dddd, LL"
                        label={t("form.date.label")}
                        loading={isCurrentMonthPending}
                        renderLoading={() => <CircularProgress size={20} />}
                        // overwide props
                        {...props}
                        // forced props
                        inputRef={inputRef}
                        shouldDisableDate={(date) => {
                            return !validDates.includes(toDateString(date));
                        }}
                        slotProps={{
                            textField: {
                                "aria-label": "change date",
                                fullWidth: true,
                                InputProps: {
                                    placeholder: "",
                                    startAdornment: (
                                        <IconButton onClick={() => inputRef.current?.click()} disabled={isDisabled}>
                                            <CalendarTodayOutlinedIcon />
                                        </IconButton>
                                    ),
                                    endAdornment: isLoading ? <CircularProgress size="1rem" /> : undefined,
                                },
                            },
                            toolbar: {
                                hidden: false,
                            },
                        }}
                        onChange={(date) => {
                            if (!date) {
                                return;
                            }
                            const availabilityListItem = availabilityList.find(
                                (availabilityListItem) => availabilityListItem.date === toDateString(date)
                            );
                            if (availabilityListItem) {
                                onChange(availabilityListItem.id, false);
                            } else {
                                onChange(null, false);
                            }
                        }}
                        onMonthChange={(date) => {
                            const calendarMonth = calendarDate({ year: date.year(), month: date.month() + 1 });
                            send({
                                type: "SET_CALENDAR_MONTH",
                                calendarMonth,
                            });

                            const monthString = toMonthString(calendarMonth);
                            if (availabilityListByMonth[monthString]?.state !== "pending") {
                                return undefined;
                            }

                            return new Promise((resolve) => {
                                const sub = service.subscribe((state) => {
                                    if (state.context.availabilityListByMonth[monthString]?.state !== "pending") {
                                        sub.unsubscribe();
                                        resolve(undefined);
                                    }
                                });
                            });
                        }}
                        value={
                            availabilityListItemId
                                ? dayjs(
                                      // Availabilities come in product timeze. Do not apply timezone as it would give incorrect results
                                      availabilityList.find(
                                          (availabilityListItem) => availabilityListItem.id === availabilityListItemId
                                      )?.date
                                  )
                                : dayjs()
                        }
                    />
                </DatePickerContainer>
            </MuiPickersUtilsProvider>
            {warningMessages.dateIsInNewMonth && defaultAvailabilityListItem && (
                <WarningMessage
                    align="left"
                    warning={t("message.dateIsInNewMonth", {
                        date: dayjs(defaultAvailabilityListItem.date)
                            .tz(timeZone ?? "Etc/GMT")
                            .locale(language.codeShort)
                            .format("dddd, LL"),
                    })}
                />
            )}
            {noAvailabilityInFirstYear && <WarningMessage align="left" warning={t("message.soldOut")} />}
        </>
    );
};
